import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Drawer,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material'
import FindReplaceIcon from '@mui/icons-material/FindReplace'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloseIcon from '@mui/icons-material/Close'
import {
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin'

import CustomAudioField from '../../components/CustomAudioField'

const ReplaceWordTab = () => {
  const record = useRecordContext()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()

  const [findWord, setFindWord] = useState('')
  const [replaceWith, setReplaceWith] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [affectedSentences, setAffectedSentences] = useState([])
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const sortedAffectedSentences = React.useMemo(() => {
    return [...affectedSentences].sort((a, b) => {
      const chapterA = a.chapterOrderIndex ?? a.chapterId ?? 0
      const chapterB = b.chapterOrderIndex ?? b.chapterId ?? 0
      if (chapterA !== chapterB) {
        return chapterA - chapterB
      }

      const paragraphA = a.paragraphId ?? 0
      const paragraphB = b.paragraphId ?? 0
      if (paragraphA !== paragraphB) {
        return paragraphA - paragraphB
      }

      const sentenceA = a.orderIndex ?? 0
      const sentenceB = b.orderIndex ?? 0
      return sentenceA - sentenceB
    })
  }, [affectedSentences])
  const chapterKey = React.useCallback(sentence => {
    if (!sentence) return 'unknown'
    return sentence.chapterOrderIndex ?? sentence.chapterId ?? 'unknown'
  }, [])

  const lazyTimerRef = React.useRef(null)
  const findTimerRef = React.useRef(null)

  // Aside panel state (similar to SentencesTab)
  const [selectedSentence, setSelectedSentence] = useState(null)
  const [showAsidePanel, setShowAsidePanel] = useState(false)
  const [sentenceFormData, setSentenceFormData] = useState({
    speakRate: '',
    contentWithBreaks: '',
  })
  const [sentenceVoiceData, setSentenceVoiceData] = useState(null)
  const [reloadAudioTick, setReloadAudioTick] = useState(0)

  // Voice selection from record voices
  const availableVoices = record?.voices || []
  const [selectedVoice, setSelectedVoice] = useState('')
  const [findOriginal, setFindOriginal] = useState(false)
  React.useEffect(() => {
    if (availableVoices.length > 0 && !selectedVoice) {
      setSelectedVoice(availableVoices[0])
    }
  }, [availableVoices, selectedVoice])

  // Helper like SentencesTab
  const getVoiceSentenceData = (sentence, currentVoice) => {
    if (!sentence?.voices || !currentVoice) {
      return {
        speakRate: sentence?.speakRate || 0,
        qcDiffResult: sentence?.qcDiffResult || [],
        qcAccuracyPercentage: sentence?.qcAccuracyPercentage || 0,
        status: sentence?.status || 'processing',
        audioPath: sentence?.audioPath || '',
      }
    }

    const voiceObj = sentence.voices.find(v => v.voice === currentVoice)
    console.log('voiceObj:', voiceObj)
    return {
      speakRate: voiceObj?.speakRate || sentence?.speakRate || 0,
      qcDiffResult: voiceObj?.qcDiffResult || sentence?.qcDiffResult || [],
      qcAccuracyPercentage:
        voiceObj?.qcAccuracyPercentage || sentence?.qcAccuracyPercentage || 0,
      status: voiceObj?.status || sentence?.status || 'processing',
      audioPath: voiceObj?.audioPath || sentence?.audioPath || '',
    }
  }

  const handleRowClick = async sentence => {
    try {
      let target = sentence

      const res = await dataProvider.getOne('ai-book-sentences', {
        id: sentence.orderIndex,
        bookId: record?.id,
        chapterId: sentence.chapterId,
        paragraphId: sentence.paragraphId,
      })
      target = res.data
      setSelectedSentence(target)
      setShowAsidePanel(true)
      setSentenceFormData({
        speakRate: target.speakRate || '',
        contentWithBreaks: target.contentWithBreaks || target.content || '',
      })
      // Gọi api lấy sentence-voice detail
      setSentenceVoiceData(null) // reset trước khi fetch mới
      if (
        selectedVoice &&
        target.chapterId &&
        target.paragraphId !== undefined &&
        target.orderIndex !== undefined
      ) {
        try {
          const resp = await dataProvider.create(
            'ai-books/get-sentence-voice',
            {
              data: {
                bookId: record?.id,
                chapterId: target.chapterId,
                voiceId: selectedVoice,
                paragraphOrder: target.paragraphId,
                sentenceOrder: target.orderIndex,
              },
            },
          )
          if (resp?.data?.data) {
            setSentenceVoiceData(resp.data.data)
          } else {
            setSentenceVoiceData(null)
          }
        } catch (e) {
          console.error('Error fetching sentence voice data', e)
          setSentenceVoiceData(null)
        }
      }
    } catch (e) {
      console.error('Error loading sentence details:', e)
      notify('Error loading sentence details', { type: 'error' })
    }
  }

  const handleCloseAsidePanel = () => {
    setShowAsidePanel(false)
    setSelectedSentence(null)
    setSentenceFormData({ speakRate: '', contentWithBreaks: '' })
  }

  const handleReloadSentence = async () => {
    try {
      if (!selectedSentence) {
        notify('No sentence selected', { type: 'error' })
        return
      }
      setLoading(true)
      const response = await dataProvider.getOne('ai-book-sentences', {
        id: selectedSentence.id,
        bookId: record?.id,
        chapterId: selectedSentence.chapterId,
        paragraphId: selectedSentence.paragraphId,
      })
      if (response.data) {
        setSelectedSentence(response.data)
        setSentenceFormData({
          speakRate: response.data.speakRate || '',
          contentWithBreaks:
            response.data.contentWithBreaks || response.data.content || '',
        })
        notify('Sentence data reloaded successfully', { type: 'success' })
        if (
          selectedVoice &&
          response.data.chapterId &&
          response.data.paragraphId !== undefined &&
          response.data.orderIndex !== undefined
        ) {
          try {
            const resp = await dataProvider.create(
              'ai-books/get-sentence-voice',
              {
                data: {
                  bookId: record?.id,
                  chapterId: response.data.chapterId,
                  voiceId: selectedVoice,
                  paragraphOrder: response.data.paragraphId,
                  sentenceOrder: response.data.orderIndex,
                },
              },
            )
            if (resp?.data?.data) {
              setSentenceVoiceData(resp.data.data)
              setReloadAudioTick(prev => prev + 1)
            } else {
              setSentenceVoiceData(null)
              setReloadAudioTick(prev => prev + 1)
            }
          } catch (e) {
            console.error('Error fetching sentence voice data (reload)', e)
            setSentenceVoiceData(null)
            setReloadAudioTick(prev => prev + 1)
          }
        }
      }
    } catch (e) {
      console.error('Error reloading sentence:', e)
      notify('Error reloading sentence data', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleReplaceButtonClick = () => {
    if (!findWord || !replaceWith) {
      notify('Please enter both words', { type: 'warning' })
      return
    }
    setShowConfirmDialog(true)
  }

  const handleConfirmReplace = async () => {
    setShowConfirmDialog(false)

    try {
      setLoading(true)
      setResult(null)

      // Call API to replace word in sentences
      const response = await dataProvider.create('ai-books/replace-word', {
        data: {
          bookId: record?.id,
          word: findWord,
          newWord: replaceWith,
        },
      })

      setResult({
        success: true,
        message: `Successfully replaced "${findWord}" with "${replaceWith}" in ${response.data?.count || 0} sentences`,
        count: response.data?.count || 0,
      })

      // Normalize affected sentences list from response
      let sentencesFromResponse = []
      if (Array.isArray(response.data?.data)) {
        // Shape: { success, total, data: [ { chapterId, paragraphOrder, sentenceOrder, contentWithBreaks } ] }
        sentencesFromResponse = response.data.data.map(item => ({
          chapterId: item.chapterId,
          paragraphId: item.paragraphOrder,
          orderIndex: item.sentenceOrder,
          content: item.contentWithBreaks || '',
        }))
      } else if (Array.isArray(response.data?.sentences)) {
        sentencesFromResponse = response.data.sentences
      } else if (Array.isArray(response.data?.data?.sentences)) {
        sentencesFromResponse = response.data.data.sentences
      }
    } catch (error) {
      console.error('Error replacing words:', error)
      const errorMessage = error?.message || 'Failed to replace words'

      setResult({
        success: false,
        message: errorMessage,
      })

      notify(errorMessage, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleCancelReplace = () => {
    setShowConfirmDialog(false)
  }

  // Cleanup pending timer on unmount
  React.useEffect(() => {
    return () => {
      if (lazyTimerRef.current) {
        clearTimeout(lazyTimerRef.current)
      }
      if (findTimerRef.current) {
        clearTimeout(findTimerRef.current)
      }
    }
  }, [])

  const handleFindClick = async () => {
    if (!findWord) {
      notify('Please enter a word to find', { type: 'warning' })
      return
    }

    try {
      setLoading(true)
      setResult(null)

      const endpoint = findOriginal
        ? 'ai-books/find-sentences-original'
        : 'ai-books/find-sentences'

      const response = await dataProvider.create(endpoint, {
        data: {
          bookId: record?.id,
          word: findWord,
          caseSensitive: true,
        },
      })

      // Normalize list from response shape: { success, total, data: [...] }
      let sentencesFromResponse = []
      if (Array.isArray(response.data?.data)) {
        sentencesFromResponse = response.data.data.map(item => ({
          chapterId: item.chapterId,
          chapterOrderIndex: item.chapterOrderIndex,
          paragraphId: item.paragraphOrder,
          orderIndex: item.sentenceOrder,
          content: item.contentWithBreaks || '',
        }))
      } else if (Array.isArray(response.data?.sentences)) {
        sentencesFromResponse = response.data.sentences
      }
      setAffectedSentences(sentencesFromResponse)

      setResult({
        success: true,
        message: `Found ${sentencesFromResponse.length} sentences containing "${findWord}"`,
        count: sentencesFromResponse.length,
      })
    } catch (error) {
      console.error('Error finding sentences:', error)
      const errorMessage = error?.message || 'Failed to find sentences'
      setResult({ success: false, message: errorMessage })
      notify(errorMessage, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateSentenceAndTriggerTTS = async (
    selectedSentence,
    setSentenceFormData,
    sentenceVoiceData,
  ) => {
    if (!selectedSentence) return
    setLoading(true)
    try {
      const payload = {
        contentWithBreaks: sentenceFormData.contentWithBreaks,
        manualAudioPath: null,
        bookId: record?.id,
        chapterId: selectedSentence.chapterId,
        paragraphId: selectedSentence.paragraphId,
        speakRate: sentenceVoiceData.speakRate || 0,
        id: selectedSentence.id,
        voiceId: sentenceVoiceData.voiceId,
      }
      await dataProvider.update('ai-book-sentences', {
        id: selectedSentence.id,
        data: payload,
      })
      notify('Sentence updated and TTS triggered successfully', {
        type: 'success',
      })
      refresh()
    } catch (error) {
      notify('Error updating sentence and triggering TTS', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    const fetchSignedUrl = async () => {
      try {
        const ap = sentenceVoiceData?.audioPath
        if (!ap) return
        const response = await dataProvider.create('ai-books/get-signed-url', {
          data: { audioPath: ap },
        })
        const signedUrl = response?.data?.signedUrl || ''
        setSentenceVoiceData(prev =>
          prev ? { ...prev, signedUrl } : { signedUrl },
        )
      } catch (e) {
        console.error('Error getting signed URL after reload:', e)
        setSentenceVoiceData(prev =>
          prev ? { ...prev, signedUrl: '' } : { signedUrl: '' },
        )
      }
    }
    if (showAsidePanel && selectedSentence) {
      fetchSignedUrl()
    }
  }, [reloadAudioTick])

  if (!record?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">No book selected</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Replace Word
          </Typography>

          {/* Row 1: Find word + Find button */}
          <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 1 }}>
            <Grid item xs={12} md={9}>
              <TextField
                label="Find Word"
                variant="outlined"
                fullWidth
                value={findWord}
                onChange={e => setFindWord(e.target.value)}
                placeholder="Enter word to find..."
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={findOriginal}
                    onChange={e => setFindOriginal(e.target.checked)}
                    size="small"
                    color="success"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Original
                  </Typography>
                }
                sx={{
                  mr: 1,
                  ml: 0,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: findOriginal
                    ? 'success.lighter'
                    : 'grey.100',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'grey.200',
                  },
                }}
              />
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<SearchIcon />}
                onClick={handleFindClick}
                disabled={!findWord}
                sx={{ textTransform: 'none', height: '40px' }}>
                Find
              </Button>
            </Grid>
          </Grid>

          {/* Row 2: Replace with + Replace button */}
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={9}>
              <TextField
                label="Replace With"
                variant="outlined"
                fullWidth
                value={replaceWith}
                onChange={e => setReplaceWith(e.target.value)}
                placeholder="Enter replacement word..."
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<FindReplaceIcon />}
                onClick={handleReplaceButtonClick}
                disabled={!findWord || !replaceWith || findOriginal}
                sx={{
                  textTransform: 'none',
                  height: '40px',
                  boxShadow: 2,
                  '&:hover': { boxShadow: 4 },
                }}>
                Replace
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Affected Sentences */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Affected Sentences ({affectedSentences.length})
          </Typography>
          {availableVoices.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Voice</InputLabel>
                <Select
                  value={selectedVoice}
                  label="Voice"
                  onChange={e => setSelectedVoice(e.target.value)}>
                  {availableVoices.map(voice => (
                    <MenuItem key={voice} value={voice}>
                      {voice}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {loading ? (
            <Typography variant="body2">Processing...</Typography>
          ) : affectedSentences.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No affected sentences to display.
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Chapter</TableCell>
                    <TableCell>Paragraph</TableCell>
                    <TableCell>Sentence</TableCell>
                    <TableCell>Content</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedAffectedSentences.map((s, idx) => {
                    const prevSentence =
                      idx > 0 ? sortedAffectedSentences[idx - 1] : null
                    const isNewChapter =
                      idx === 0 || chapterKey(s) !== chapterKey(prevSentence)

                    const rowKey = `${s.id || ''}-${s.chapterId}-${s.paragraphId}-${s.orderIndex}-${idx}`
                    const chapterLabel = chapterKey(s)

                    return (
                      <React.Fragment key={rowKey}>
                        {isNewChapter && (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              sx={{
                                bgcolor: 'grey.100',
                                borderTop: theme =>
                                  `2px solid ${theme.palette.divider}`,
                              }}>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary">
                                Chapter {chapterLabel}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        <TableRow
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleRowClick(s)}>
                          <TableCell>{s.chapterOrderIndex}</TableCell>
                          <TableCell>{s.paragraphId}</TableCell>
                          <TableCell>{s.orderIndex}</TableCell>

                          <TableCell sx={{ maxWidth: 600 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}>
                              {s.content}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Aside Right Panel (Sentence Details) */}
      <Drawer
        anchor="right"
        open={showAsidePanel}
        onClose={handleCloseAsidePanel}
        sx={{
          '& .MuiDrawer-paper': { width: 500, padding: 2 },
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}>
          <Typography variant="h6">Sentence Details</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={handleReloadSentence}
              title="Reload sentence data">
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={handleCloseAsidePanel}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {selectedSentence ? (
          <Box sx={{ height: '100%', overflow: 'auto' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Sentence Text"
                  multiline
                  rows={4}
                  variant="outlined"
                  fullWidth
                  value={selectedSentence.content || ''}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Sentence Text With Breaks"
                  multiline
                  rows={4}
                  variant="outlined"
                  fullWidth
                  value={
                    sentenceFormData.contentWithBreaks ||
                    selectedSentence.contentWithBreaks ||
                    selectedSentence.content ||
                    ''
                  }
                  onChange={e =>
                    setSentenceFormData(prev => ({
                      ...prev,
                      contentWithBreaks: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`Speak Rate${selectedVoice ? ` (${selectedVoice})` : ''}`}
                  variant="outlined"
                  fullWidth
                  value={sentenceVoiceData?.speakRate ?? ''}
                  onChange={e =>
                    setSentenceFormData(prev => ({
                      ...prev,
                      speakRate: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`QC Accuracy Percentage${selectedVoice ? ` (${selectedVoice})` : ''}`}
                  variant="outlined"
                  fullWidth
                  value={
                    sentenceVoiceData?.qcAccuracyPercentage ??
                    getVoiceSentenceData(selectedSentence, selectedVoice)
                      ?.qcAccuracyPercentage ??
                    ''
                  }
                  disabled
                />
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Status {selectedVoice && `(${selectedVoice})`}
                </Typography>
                <Chip
                  label={
                    sentenceVoiceData?.status ??
                    getVoiceSentenceData(selectedSentence, selectedVoice)
                      ?.status ??
                    ''
                  }
                  color={
                    sentenceVoiceData?.status === 'completed' ||
                    getVoiceSentenceData(selectedSentence, selectedVoice)
                      ?.status === 'completed'
                      ? 'success'
                      : 'warning'
                  }
                  variant="outlined"
                />
              </Grid>
              {(sentenceVoiceData?.audioPath ??
                getVoiceSentenceData(selectedSentence, selectedVoice)
                  ?.audioPath ??
                '') && (
                <Grid item xs={9}>
                  <CustomAudioField
                    label={`Sentence Audio${selectedVoice ? ` (${selectedVoice})` : ''}`}
                    signedUrl={sentenceVoiceData?.signedUrl}
                    audioPath={
                      sentenceVoiceData?.audioPath ??
                      getVoiceSentenceData(selectedSentence, selectedVoice)
                        ?.audioPath ??
                      ''
                    }
                    inline={true}
                  />
                </Grid>
              )}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    handleUpdateSentenceAndTriggerTTS(
                      selectedSentence,
                      sentenceFormData,
                      sentenceVoiceData,
                    )
                  }
                  disabled={loading}
                  fullWidth
                  sx={{ py: 1.5 }}>
                  {loading
                    ? 'Processing...'
                    : 'Update Sentence And Trigger TTS'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No sentence selected
          </Typography>
        )}
      </Drawer>

      {/* Confirm Replace Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelReplace}
        aria-labelledby="confirm-replace-dialog-title"
        aria-describedby="confirm-replace-dialog-description">
        <DialogTitle id="confirm-replace-dialog-title">
          Confirm Replace
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-replace-dialog-description">
            Are you sure you want to replace all occurrences of "
            <strong>{findWord}</strong>" with "<strong>{replaceWith}</strong>"
            in this book?
            <br />
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReplace} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReplace}
            color="primary"
            variant="contained"
            autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ReplaceWordTab
